import {
  createDocumentDataFieldGroup,
  toDropdownOptions,
} from '../../models/extraction-models/extraction-models';
import { FieldType } from '@ps-gui/shared-utils';
import {
  befundArtOptions,
  BehandlungArt,
  behandlungsArtToLabel,
  behinderungsGradToLabel,
  Fachrichtung,
  Merkzeichen,
  merkzeichenToText,
  pflegegradDropdownOptions,
} from '@ps-gui/shared-core';
import { ExtractionSection } from '../domain-extraction-specifications';
import { Validations } from '@ps-gui/ui';

export const FragebogenSectionPage1: ExtractionSection[] = [
  createDocumentDataFieldGroup(),
  {
    key: 'page1',
    label: 'Seite 1',
    index: 0,
    type: 'fieldGroupModel',
    fieldModels: [
      {
        key: 'page1_behandlung_von',
        label: {
          text: 'Behandlung Von',
          genus: 'F',
        },
        type: FieldType.DATE,
        index: 1,
      },
      {
        key: 'page1_behandlung_bis',
        label: {
          text: 'Behandlung Bis',
          genus: 'F',
        },
        type: FieldType.DATE,
        index: 2,
      },
      {
        key: 'page1_behandlungstyp',
        label: {
          text: 'Behandlungstyp',
          genus: 'M',
        },
        type: FieldType.DROPDOWN,
        options: [
          { label: 'Ambulant', value: 'AMBULANT' },
          { label: 'Stationär', value: 'STATIONAER' },
          { label: 'Unbekannt', value: 'UNBEKANNT' },
        ],
        index: 3,
      },
      {
        key: 'page1_unfallhergang',
        label: {
          text: 'Unfallhergang',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 4,
      },
      {
        key: 'page1_beschwerden',
        label: {
          text: 'Beschwerden',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 5,
      },
      {
        key: 'page1_beschwerden_aufgetreten_am',
        label: {
          text: 'Beschwerden aufgetreten am',
          genus: 'F',
        },
        type: FieldType.DATE,
        index: 6,
      },
      {
        key: 'page1_arbeitsunfaehigkeit_von',
        label: {
          text: 'Arbeitsunfähigkeit von',
          genus: 'F',
        },
        type: FieldType.DATE,
        index: 7,
      },
      {
        key: 'page1_arbeitsunfaehigkeit_bis',
        label: {
          text: 'Arbeitsunfähigkeit bis',
          genus: 'F',
        },
        type: FieldType.DATE,
        index: 8,
      },
      {
        key: 'page1_beruf',
        label: {
          text: 'Beruf',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 9,
      },
      {
        key: 'page1_kostentraeger',
        label: {
          text: 'Kostenträger',
          genus: 'M',
        },
        type: FieldType.DROPDOWN,
        options: [
          { label: 'Ges. KV', value: 'GKV' },
          { label: 'Ges. Unfallvers. (BG, Unfallkasse)', value: 'GES_UV' },
          { label: 'Privat', value: 'PKV' },
        ],
        index: 10,
      },
      {
        key: 'page1_name_des_kostentraegers',
        label: {
          text: 'Name des Kostenträgers',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 11,
      },
      {
        key: 'page1_gdb',
        label: {
          text: 'GdB',
          genus: 'M',
        },
        type: FieldType.DROPDOWN,
        options: toDropdownOptions([...behinderungsGradToLabel.values()]),
        index: 12,
      },
      {
        key: 'page1_merkzeichen',
        label: {
          text: 'Merkzeichen',
          genus: 'N',
        },
        type: FieldType.DROPDOWN,
        options: [
          ...merkzeichenToText
            .entries()
            .map((entry: [Merkzeichen, string]) => ({ value: entry[0], label: entry[1] })),
        ],
        index: 12,
      },
      {
        key: 'page1_mde',
        label: {
          text: 'MdE',
          genus: 'F',
        },
        type: FieldType.NUMBER,
        index: 13,
        validators: [Validations.naturalNumber()],
      },
      {
        key: 'page1_pflegegrad',
        label: {
          text: 'Pflegegrad',
          genus: 'M',
        },
        type: FieldType.DROPDOWN,
        options: pflegegradDropdownOptions.map((d) => ({
          value: d.value.toString(),
          label: d.label,
        })),
        index: 14,
      },
    ],
  },
];

export const FragebogenSectionPage2: ExtractionSection[] = [
  {
    key: 'page2_befunde',
    type: 'tableModel',
    label: 'Befunde',
    fieldModels: [
      {
        key: 'page2_befunde_art',
        label: { text: 'Art', genus: 'F' },
        type: FieldType.DROPDOWN,
        options: befundArtOptions,
        index: 0,
        widthUtilityClass: 'w-4/12',
      },
      {
        key: 'page2_befunde_beschreibung',
        label: { text: 'Beschreibung', genus: 'F' },
        type: FieldType.STRING,
        index: 1,
        widthUtilityClass: 'w-8/12',
      },
    ],
    tableValidators: [],
    index: 0,
  },
  {
    key: 'page2_diagnosen',
    type: 'tableModel',
    label: 'Diagnosen',
    fieldModels: [
      {
        key: 'page2_diagnosen_icd10_code',
        label: { text: 'ICD-10 Code', genus: 'M' },
        type: FieldType.STRING,
        index: 2,
        widthUtilityClass: 'w-4/12',
      },
      {
        key: 'page2_diagnosen_klartext',
        label: { text: 'Klartext', genus: 'M' },
        type: FieldType.STRING,
        index: 3,
        widthUtilityClass: 'w-8/12',
      },
    ],
    tableValidators: [],
    index: 1,
  },
  {
    key: 'page2_behandlung',
    type: 'tableModel',
    label: 'Behandlung',
    fieldModels: [
      {
        key: 'page2_behandlung_art',
        label: { text: 'Art', genus: 'F' },
        type: FieldType.DROPDOWN,
        options: [
          ...behandlungsArtToLabel
            .entries()
            .map((entry: [BehandlungArt, string]) => ({ value: entry[0], label: entry[1] })),
        ],
        index: 4,
        widthUtilityClass: 'w-3/12',
      },
      {
        key: 'page2_behandlung_datum',
        label: { text: 'Datum', genus: 'N' },
        type: FieldType.DATE,
        index: 5,
        widthUtilityClass: 'w-3/12',
        validators: [Validations.date()],
      },
      {
        key: 'page2_behandlung_beschreibung',
        label: { text: 'Beschreibung', genus: 'N' },
        type: FieldType.STRING,
        index: 6,
        widthUtilityClass: 'w-6/12',
      },
    ],
    tableValidators: [],
    index: 2,
  },
  {
    key: 'page2_hinzugezogen',
    label: 'Hinzugezogen',
    index: 3,
    type: 'fieldGroupModel',
    fieldModels: [
      {
        key: 'page2_hinzugezogen_vorname',
        label: {
          text: 'Vorname (Hinzugezogen)',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 7,
      },
      {
        key: 'page2_hinzugezogen_nachname',
        label: {
          text: 'Nachname (Hinzugezogen)',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 8,
      },
      {
        key: 'page2_hinzugezogen_fachrichtung',
        label: {
          text: 'Fachrichtung (Hinzugezogen)',
          genus: 'F',
        },
        type: FieldType.DROPDOWN,
        options: getFachrichtungenDropdownOptions(),
        index: 9,
      },
      {
        key: 'page2_strassehausnummer',
        label: {
          text: 'Straße und Hausnummer (Hinzugezogen)',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 10,
      },
      {
        key: 'page2_hinzugezogen_plz',
        label: {
          text: 'PLZ (Hinzugezogen)',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 11,
      },
      {
        key: 'page2_hinzugezogen_ort',
        label: {
          text: 'Ort (Hinzugezogen)',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 12,
      },
    ],
  },
];

export const FragebogenSectionPage3: ExtractionSection[] = [
  {
    key: 'page3',
    label: 'Seite 3',
    index: 0,
    type: 'fieldGroupModel',
    fieldModels: [
      {
        key: 'page3_weiterbehandler_einrichtung',
        label: {
          text: 'Weiterbehandler Einrichtung',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 2,
      },
      {
        key: 'page3_weiterbehandler_vorname',
        label: {
          text: 'Weiterbehandler Vorname',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 3,
      },
      {
        key: 'page3_weiterbehandler_nachname',
        label: {
          text: 'Weiterbehandler Nachname',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 4,
      },
      {
        key: 'page3_weiterbehandler_strassehausnummer',
        label: {
          text: 'Weiterbehandler Straße und Hausnummer',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 5,
      },
      {
        key: 'page3_weiterbehandler_plz',
        label: {
          text: 'Weiterbehandler Postleitzahl',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 6,
      },
      {
        key: 'page3_weiterbehandler_ort',
        label: {
          text: 'Weiterbehandler Ort',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 7,
      },
      {
        key: 'page3_weitere_behandlungsdauer',
        label: {
          text: 'Weitere Behandlungsdauer',
          genus: 'F',
        },
        type: FieldType.DROPDOWN,
        options: [
          { label: 'Bis 3 Monate', value: 'BIS_3_MONATE' },
          { label: '3 - 6 Monate', value: 'ZWISCHEN_3_UND_6_MONATE' },
          { label: 'Über 6 Monate', value: 'UEBER_6_MONATE' },
        ],
        index: 8,
      },
      {
        key: 'page3_datum_letzte_konsultation',
        label: {
          text: 'Datum letzte Konsultation',
          genus: 'N',
        },
        type: FieldType.DATE,
        index: 9,
      },
      {
        key: 'page3_befund_letzte_konsultation',
        label: {
          text: 'Befund letzte Konsultation',
          genus: 'M',
        },
        type: FieldType.STRING,
        index: 10,
      },
      {
        key: 'page3_stoerungen_im_heilverlauf',
        label: {
          text: 'Störungen im Heilverlauf',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 11,
      },
      {
        key: 'page3_koerperliche_geistige_einschraenkungen',
        label: {
          text: 'Körperliche/geistige Einschränkungen',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 12,
      },
      {
        key: 'page3_besserungsmassnahmen',
        label: {
          text: 'Besserungsmaßnahmen',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 13,
      },
      {
        key: 'page3_fachrichtung_zweitmeinung',
        label: {
          text: 'Fachrichtung Zweitmeinung',
          genus: 'F',
        },
        type: FieldType.DROPDOWN,
        options: getFachrichtungenDropdownOptions(),
        index: 14,
      },
    ],
  },
];

export const FragebogenSectionPage4: ExtractionSection[] = [
  {
    key: 'page4',
    label: 'Seite 4',
    index: 0,
    type: 'fieldGroupModel',
    fieldModels: [
      {
        key: 'page4_bewertungsnoten',
        label: {
          text: 'Bewertungsnoten',
          genus: 'F',
        },
        type: FieldType.DROPDOWN,
        options: toDropdownOptions(['1', '2', '3', '4', '5']),
        index: 1,
      },
      {
        key: 'page4_langfristige_folgen',
        label: {
          text: 'Langfristige Folgen',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 2,
      },
      {
        key: 'page4_datum_abschaetzung_langfristige_folgen',
        label: {
          text: 'Datum Abschätzung langfristige Folgen',
          genus: 'N',
        },
        type: FieldType.DATE,
        index: 3,
      },
    ],
  },
  {
    key: 'page4_unfallunabhaengige_diagnosen',
    type: 'tableModel',
    label: 'Unfallunabhängige Diagnosen',
    fieldModels: [
      {
        key: 'page4_unfallunabhaengige_diagnosen_icd10_code',
        label: { text: 'ICD-10 Code', genus: 'M' },
        type: FieldType.STRING,
        index: 0,
        widthUtilityClass: 'w-4/12',
      },
      {
        key: 'page4_unfallunabhaengige_diagnosen_klartext',
        label: { text: 'Klartext', genus: 'M' },
        type: FieldType.STRING,
        index: 1,
        widthUtilityClass: 'w-8/12',
      },
    ],
    tableValidators: [],
    index: 0,
  },
  {
    key: 'page4_bewusstseinsstoerung',
    label: 'Bewusstseinsstörung',
    index: 0,
    type: 'fieldGroupModel',
    fieldModels: [
      {
        key: 'page4_bewusstseinsstoerung_auswahl',
        label: {
          text: 'Auswahl',
          genus: 'F',
        },
        type: FieldType.DROPDOWN,
        options: toDropdownOptions([
          'Alkohol',
          'Drogen',
          'Medikamente',
          'Bewusstlosigkeit vor Unfall',
        ]),
        index: 1,
      },
      {
        key: 'page4_bewusstseinsstoerung_andere',
        label: {
          text: 'Andere',
          genus: 'F',
        },
        type: FieldType.STRING,
        index: 2,
      },
    ],
  },
];

function getFachrichtungenDropdownOptions() {
  return [
    ...Object.entries(Fachrichtung).map((f: [string, Fachrichtung]) => ({
      label: f[1],
      value: f[1],
    })),
  ];
}
